/**
 * The system chrome the APP decides: when the launch splash leaves, which way
 * the phone may turn, and which colour the browser's own chrome takes.
 *
 * The bar STYLE is not here — it follows the resolved theme and is applied by
 * `app-frame.tsx`, which is the one place that knows what `resolved` is.
 *
 * `capacitor.config.ts` sets `launchAutoHide: false`, so nothing but
 * {@link hideSplashAfterFirstPaint} ever takes the splash down. The two ship
 * in one APK: the config is copied into the shell at `cap sync` and is never
 * carried by an over-the-air bundle. The other direction is safe — an old
 * shell (auto-hide on) running a new bundle hides a splash that is already
 * gone, which the plugin treats as a no-op.
 *
 * Every effect is injected so the unit test runs in Node with no DOM: the
 * exported functions only supply the real ones.
 */
import { useEffect } from "react";
import {
  hideSplash,
  isNative,
  lockOrientation,
  setStatusBarTheme,
} from "../native";

/**
 * Android calls a screen a tablet at 600 dp on its short edge, and the
 * WebView's CSS pixels are dp. Below it, the phone layout is the only one that
 * exists and landscape has nothing to show.
 */
const PHONE_MAX_DP = 600;

/**
 * How long a boot may take before the splash comes down regardless. A hung
 * boot must show the error boundary, not a splash for ever.
 */
const SPLASH_SAFETY_MS = 5000;

/** Cancels a pending timer. */
type Cancel = () => void;

export interface SystemChromeEffects {
  readonly delay: (callback: () => void, ms: number) => Cancel;
  readonly hide: () => Promise<unknown>;
  readonly isNative: () => boolean;
  readonly lock: (orientation: "portrait") => Promise<unknown>;
  /** One animation frame. */
  readonly raf: (callback: () => void) => void;
  /** The screen in CSS pixels. */
  readonly screenSize: () => { height: number; width: number };
}

const realEffects: SystemChromeEffects = {
  hide: () => hideSplash(),
  isNative: () => isNative(),
  lock: (orientation) => lockOrientation(orientation),
  raf: (callback) => {
    requestAnimationFrame(callback);
  },
  // `screen`, never `window.inner*`: the keyboard and split-screen change the
  // window, and a phone that opened its keyboard is still a phone.
  screenSize: () => ({ height: screen.height, width: screen.width }),
  delay: (callback, ms) => {
    const id = setTimeout(callback, ms);
    return () => clearTimeout(id);
  },
};

/** Android's own threshold, applied to the screen's short edge. */
export function isPhoneSized(width: number, height: number): boolean {
  return Math.min(width, height) < PHONE_MAX_DP;
}

/**
 * Locks a phone to portrait. Tablets keep both orientations, and on the web
 * nothing is locked — a browser owns its own window.
 *
 * Never throws: a shell without the plugin must still boot.
 */
export function startSystemChrome(
  effects: SystemChromeEffects = realEffects
): void {
  if (!effects.isNative()) {
    return;
  }
  const { height, width } = effects.screenSize();
  if (!isPhoneSized(width, height)) {
    return;
  }
  effects.lock("portrait").catch(() => undefined);
}

/**
 * Takes the splash down once the first real frame is on screen.
 *
 * Two frames, not one: React's first `render()` has only committed to the DOM
 * by the end of the first, so the second is the first the user can see. The
 * safety timer is the other half — with auto-hide off, a boot that never
 * paints would otherwise sit behind the splash with no way out.
 *
 * Whichever wins, the splash is hidden exactly once and the loser is cancelled.
 */
export function hideSplashAfterFirstPaint(
  effects: SystemChromeEffects = realEffects
): void {
  holdSplash(effects).release();
}

/** A splash held up until {@link SplashHold.release}, or the safety timer. */
export interface SplashHold {
  /** Hide it two frames from now — the first frame the user can see. */
  release(): void;
}

/**
 * Arm the safety timer now, and hide the splash when released. Exactly once,
 * whichever comes first.
 */
export function holdSplash(
  effects: SystemChromeEffects = realEffects
): SplashHold {
  if (!effects.isNative()) {
    return { release: () => undefined };
  }
  let done = false;
  // Assigned below, and declared here because a timer that fired SYNCHRONOUSLY
  // (a test's injected `delay`) would otherwise read it in its dead zone.
  let cancelSafety: Cancel = () => undefined;
  const hideOnce = () => {
    if (done) {
      return;
    }
    done = true;
    cancelSafety();
    effects.hide().catch(() => undefined);
  };
  cancelSafety = effects.delay(hideOnce, SPLASH_SAFETY_MS);
  return { release: () => effects.raf(() => effects.raf(hideOnce)) };
}

let bootSplash: SplashHold = { release: () => undefined };

/**
 * Boot's hold: the splash stays until the session gate has DECIDED
 * (`releaseBootSplash`, `D194`), not two frames after the first render. That
 * first frame was the gate's `null` while the token store and the persisted
 * cache were read — a blank canvas between the splash and the app, longer
 * the bigger the cache. The safety timer still bounds it.
 */
export function holdBootSplash(effects: SystemChromeEffects = realEffects) {
  bootSplash = holdSplash(effects);
}

/** The session gate knows where the person goes: take the splash down. */
export function releaseBootSplash(): void {
  bootSplash.release();
}

/**
 * The canvas colour of the theme currently on `<html>`, for the browser chrome
 * (`meta[name="theme-color"]`). Read from the token rather than written down:
 * `src/` carries no colour literal (arch rule 10), and this follows an in-app
 * override that the tags' media queries cannot see.
 */
export function themeColorFromCanvas(): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue("--color-canvas")
    .trim();
}

/**
 * Status-bar styles a layer imposes over the theme's, most recent last
 * (`D193`). The retrospective paints `cel-ink` — dark in BOTH themes — so in
 * a light theme the theme's dark glyphs would sit on a dark surface.
 * `app-frame.tsx` applies the top of this stack instead of the theme while it
 * is not empty.
 */
const barOverrides: ("dark" | "light")[] = [];

/** The style the bars should take: the top override, else the theme's. */
export function statusBarStyleFor(
  resolved: "dark" | "light"
): "dark" | "light" {
  return barOverrides.at(-1) ?? resolved;
}

function resolvedOnHtml(): "dark" | "light" {
  return document.documentElement.getAttribute("data-theme") === "dark"
    ? "dark"
    : "light";
}

/**
 * Holds the status bar to `background` while mounted; `null` holds nothing.
 * Releasing re-applies whatever is now on top — another layer's override, or
 * the theme on `<html>`.
 */
export function useStatusBarOverride(
  background: "dark" | "light" | null
): void {
  useEffect(() => {
    if (background === null) {
      return;
    }
    barOverrides.push(background);
    // The layer paints under the bars itself: the app's opaque backings
    // (`.system-bar` in `index.css`) step aside while any override holds.
    document.documentElement.setAttribute("data-bar-override", "");
    setStatusBarTheme(background).catch(() => undefined);
    return () => {
      const at = barOverrides.lastIndexOf(background);
      if (at !== -1) {
        barOverrides.splice(at, 1);
      }
      if (barOverrides.length === 0) {
        document.documentElement.removeAttribute("data-bar-override");
      }
      setStatusBarTheme(statusBarStyleFor(resolvedOnHtml())).catch(
        () => undefined
      );
    };
  }, [background]);
}
